import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Box, Typography, Paper, Grid, TextField, Button, FormControl, 
    InputLabel, Select, MenuItem, CircularProgress, Alert, List, 
    ListItem, ListItemText, Divider, Chip, Modal 
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
// Per sicurezza, usiamo l'import di default che ha funzionato in altri file
import province from '../utils/province.js'; 

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 600,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto'
}; 

// Componente Filtri
const AlumniFilters = ({ onSearch }) => {
    const [filters, setFilters] = useState({ search: '', province: '', category: '' });
    const [categories, setCategories] = useState([]);
  
    useEffect(() => {
      axios.get('/api/categories')
        .then(res => setCategories(res.data))
        .catch(err => console.error("Errore nel caricare le categorie per i filtri", err));
    }, []);
  
    const handleChange = (e) => {
      setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };
  
    const handleSearch = () => {
      onSearch(filters);
    };

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="search"
              label="Cerca per nome, competenze..."
              variant="outlined"
              size="small"
              value={filters.search}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Provincia</InputLabel>
              <Select name="province" value={filters.province} label="Provincia" onChange={handleChange}>
                <MenuItem value=""><em>Tutte le province</em></MenuItem>
                {/* --- CONTROLLO DI SICUREZZA AGGIUNTO QUI --- */}
                {Array.isArray(province) && province.map(p => (
                  <MenuItem key={p.sigla} value={p.sigla}>{p.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria Competenze</InputLabel>
              <Select name="category" value={filters.category} label="Categoria Competenze" onChange={handleChange}>
                <MenuItem value=""><em>Tutte le categorie</em></MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch} startIcon={<SearchIcon />}>
              Cerca
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
};

// Pagina Principale (invariata, ma la includo per completezza)
const SearchAlumni = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  
  const [selectedAlumni, setSelectedAlumni] = useState(null);

  const handleOpenModal = (alumno) => setSelectedAlumni(alumno);
  const handleCloseModal = () => setSelectedAlumni(null);

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        setLoading(true);
        const res = await axios.get('/api/alumni', { params: activeFilters });
        setAlumni(res.data);
      } catch (err) {
        setError('Impossibile caricare la lista degli alumni.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlumni();
  }, [activeFilters]);

  const handleSearch = (filters) => {
    const newFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
    );
    setActiveFilters(newFilters);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Ricerca Alumni</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Trova i talenti più adatti alle tue esigenze tra gli alumni della Fondazione.
      </Typography>

      <AlumniFilters onSearch={handleSearch} />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Paper>
          <List>
            {alumni.length > 0 ? alumni.map((alunno, index) => (
              <React.Fragment key={alunno.id}>
                <ListItem 
                  alignItems="flex-start"
                  secondaryAction={
                    <Button variant="outlined" size="small" onClick={() => handleOpenModal(alunno)}>
                      Vedi Profilo
                    </Button>
                  }
                >
                  <ListItemText
                    primary={<Typography variant="h6">{alunno.fullName}</Typography>}
                    secondary={
                      <>
                        <Typography component="p" variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          <strong>Provincia:</strong> {alunno.province || 'Non specificata'}
                        </Typography>
                        <Typography component="p" variant="body2" color="text.primary">
                          <strong>Ambito di ricerca:</strong> {alunno.desiredJob || 'Non specificato'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {alunno.categories.map(c => (
                            <Chip key={c.category.name} label={c.category.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      </>
                    }
                  />
                </ListItem>
                {index < alumni.length - 1 && <Divider component="li" />}
              </React.Fragment>
            )) : (
              <Typography sx={{ p: 2, textAlign: 'center' }}>
                Nessun alunno trovato con i criteri di ricerca attuali.
              </Typography>
            )}
          </List>
        </Paper>
      )}
      
      {/* Modal Profilo Completo */}
      <Modal open={!!selectedAlumni} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          {selectedAlumni && (
            <>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedAlumni.fullName}
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>📧 Email:</strong> {selectedAlumni.email || 'Non disponibile'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>📍 Provincia:</strong> {selectedAlumni.province || 'Non specificata'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>🎓 Formazione:</strong> {selectedAlumni.education || 'Non specificata'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>💼 Ambito ricerca:</strong> {selectedAlumni.desiredJob || 'Non specificato'}
              </Typography>
              {selectedAlumni.linkedin && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>🔗 LinkedIn:</strong>{' '}
                  <a href={selectedAlumni.linkedin} target="_blank" rel="noopener noreferrer">
                    {selectedAlumni.linkedin}
                  </a>
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                🛠️ Competenze:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedAlumni.categories.map((c, idx) => (
                  <Chip key={idx} label={c.category.name} color="primary" size="small" />
                ))}
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleCloseModal}>
                  Chiudi
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default SearchAlumni;